import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  IconButton,
  Chip,
  Stack,
  CircularProgress,
  Alert,
  Tooltip
} from '@mui/material';
import {
  Refresh as RefreshIcon,
  Fullscreen as FullscreenIcon,
  SignalWifiOff as OfflineIcon,
  Videocam as VideocamIcon,
  LocationOn as LocationIcon
} from '@mui/icons-material';
import streamsApi from '../../api/streamsApi'; // Import the streamsApi

/**
 * StreamViewer Component
 * Displays a single live video stream from an ENViS device
 */
const StreamViewer = ({
  device,
  onRefresh,
  maxHeight = 300,
  showControls = true,
  showDeviceInfo = true
}) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isOnline, setIsOnline] = useState(false);
  const [ticketedUrl, setTicketedUrl] = useState(null);
  const videoRef = useRef(null);
  const retryTimeoutRef = useRef(null);
  const [retryCount, setRetryCount] = useState(0);
  const maxRetries = 3; // Set a max number of retries

  // Determine the correct, final URL for the stream (proxied or direct)
  useEffect(() => {
    const determineStreamUrl = async () => {
      if (device?.id && device?.stream_url) {
        try {
          setLoading(true);
          setError(null);
          
          // If the URL is insecure (http), get a secure ticket for our proxy
          if (device.stream_url.startsWith('http://')) {
            const url = await streamsApi.getStreamTicket(device.id);
            setTicketedUrl(url);
          }
          // If the URL is already secure (https), use it directly
          else if (device.stream_url.startsWith('https://')) {
            setTicketedUrl(device.stream_url);
            setLoading(false); // No further loading needed
          }
          // Handle invalid URL formats
          else {
            setError('Invalid stream URL format.');
            setLoading(false);
          }
        } catch (err) {
          setError('Could not get stream ticket.');
          setLoading(false);
        }
      } else {
        // Handle cases where there is no stream URL
        setLoading(false);
      }
    };

    determineStreamUrl();
  }, [device?.id, device?.stream_url]);

  // Handle stream loading once the ticketed URL is available
  useEffect(() => {
    if (ticketedUrl && videoRef.current) {
      const video = videoRef.current;
      
      const handleLoadStart = () => setLoading(true);
      const handleCanPlay = () => {
        setLoading(false);
        setIsOnline(true);
        setError(null);
        setRetryCount(0); // Reset retries on successful connection
      };
      const handleError = () => {
        setIsOnline(false);
        if (retryCount < maxRetries) {
          console.log(`Stream for device ${device.id} failed. Retrying in 3 seconds... (Attempt ${retryCount + 1})`);
          // Clear any existing timeout
          if (retryTimeoutRef.current) {
            clearTimeout(retryTimeoutRef.current);
          }
          // Set a timeout to retry after a delay
          retryTimeoutRef.current = setTimeout(() => {
            setRetryCount(retryCount + 1);
            handleRefresh();
          }, 3000); // 3-second delay
        } else {
          console.error(`Stream for device ${device.id} failed after ${maxRetries} retries. Giving up.`);
          setError('Failed to load video stream. Device may be offline.');
          setLoading(false);
        }
      };
      
      video.addEventListener('loadstart', handleLoadStart);
      video.addEventListener('canplay', handleCanPlay);
      video.addEventListener('error', handleError);
      
      // Set the video source to the ticketed URL
      video.src = ticketedUrl;
      
      return () => {
        video.removeEventListener('loadstart', handleLoadStart);
        video.removeEventListener('canplay', handleCanPlay);
        video.removeEventListener('error', handleError);
      };
    }
  }, [ticketedUrl]);

  const handleRefresh = async (isManual = false) => {
    if (isManual) {
      setRetryCount(0); // Reset retries if the user manually refreshes
    }
    if (device?.id && device?.stream_url) {
      try {
        setLoading(true);
        setError(null);
        const url = await streamsApi.getStreamTicket(device.id);
        setTicketedUrl(url);
        // Manually trigger the video to load the new source
        if (videoRef.current) {
          videoRef.current.src = url;
          videoRef.current.load();
        }
      } catch (err) {
        setError('Could not get stream ticket.');
        setLoading(false);
      }
    }
    if (onRefresh && isManual) {
      onRefresh(device.id);
    }
  };

  const handleFullscreen = () => {
    // Open stream in a new window/tab for better fullscreen experience
    if (ticketedUrl) {
      const fullscreenWindow = window.open(
        ticketedUrl,
        '_blank',
        'width=' + window.screen.width + ',height=' + window.screen.height + ',fullscreen=yes,scrollbars=no,toolbar=no,location=no,status=no,menubar=no'
      );
      
      // Try to make it fullscreen if possible
      if (fullscreenWindow) {
        fullscreenWindow.focus();
      }
    }
  };

  if (!device?.stream_url) {
    return (
      <Card sx={{ height: maxHeight, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <CardContent>
          <Stack spacing={2} alignItems="center">
            <OfflineIcon sx={{ fontSize: 48, color: 'text.secondary' }} />
            <Typography variant="body2" color="text.secondary" textAlign="center">
              No stream URL configured for this device
            </Typography>
            {showDeviceInfo && (
              <Typography variant="caption" color="text.secondary">
                {device.device_name}
              </Typography>
            )}
          </Stack>
        </CardContent>
      </Card>
    );
  }

  const cardStyle = {
    height: maxHeight,
    position: 'relative',
    overflow: 'hidden',
    borderRadius: 2,
    display: 'flex',
    flexDirection: 'column',
    boxShadow: '0 6px 20px rgba(0, 0, 0, 0.05)',
    transition: 'transform 0.3s ease, box-shadow 0.3s ease',
    '&:hover': {
      transform: 'translateY(-4px)',
      boxShadow: '0 12px 30px rgba(0, 0, 0, 0.08)',
    }
  };

  return (
    <Card sx={cardStyle}>
      {/* Stream Video */}
      <Box sx={{ 
        position: 'relative', 
        height: showDeviceInfo ? `calc(100% - 90px)` : '100%',
        bgcolor: 'black',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center'
      }}>
        {loading && (
          <Box sx={{ 
            position: 'absolute', 
            top: '50%', 
            left: '50%', 
            transform: 'translate(-50%, -50%)',
            zIndex: 2
          }}>
            <CircularProgress color="primary" />
          </Box>
        )}
        
        {error && (
          <Alert 
            severity="error" 
            sx={{ 
              position: 'absolute', 
              top: '50%', 
              left: '50%', 
              transform: 'translate(-50%, -50%)',
              zIndex: 2,
              maxWidth: '90%'
            }}
          >
            {error}
          </Alert>
        )}
        
        {/* Use an img tag for MJPEG streams, which is the standard and most reliable method */}
        {ticketedUrl && !error && (
          <img
            src={ticketedUrl}
            alt={`${device.device_name} Stream`}
            style={{
              width: '100%',
              height: '100%',
              objectFit: 'contain',
              display: loading ? 'none' : 'block'
            }}
            onLoad={() => {
              setLoading(false);
              setIsOnline(true);
              setError(null);
            }}
            onError={() => {
              setLoading(false);
              setIsOnline(false);
              setError('Failed to load video stream');
            }}
          />
        )}
        
        {/* Stream Controls Overlay */}
        {showControls && (
          <Box sx={{
            position: 'absolute',
            top: 8,
            right: 8,
            display: 'flex',
            gap: 1,
            opacity: 0.8,
            '&:hover': { opacity: 1 }
          }}>
            <Tooltip title="Refresh Stream">
              <IconButton 
                size="small" 
                onClick={() => handleRefresh(true)}
                sx={{ 
                  bgcolor: 'rgba(0,0,0,0.6)', 
                  color: 'white',
                  '&:hover': { bgcolor: 'rgba(0,0,0,0.8)' }
                }}
              >
                <RefreshIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            
            <Tooltip title="Fullscreen">
              <IconButton 
                size="small" 
                onClick={handleFullscreen}
                sx={{ 
                  bgcolor: 'rgba(0,0,0,0.6)', 
                  color: 'white',
                  '&:hover': { bgcolor: 'rgba(0,0,0,0.8)' }
                }}
              >
                <FullscreenIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>
        )}
        
        {/* Status Indicator */}
        <Box sx={{
          position: 'absolute',
          top: 8,
          left: 8,
          display: 'flex',
          gap: 1,
          opacity: 0.9
        }}>
          <Chip
            icon={isOnline ? <VideocamIcon /> : <OfflineIcon />}
            label={isOnline ? 'LIVE' : 'OFFLINE'}
            size="small"
            color={isOnline ? 'success' : 'error'}
            sx={{ 
              fontWeight: 'bold',
              fontSize: '0.7rem',
              bgcolor: isOnline ? 'rgba(76, 175, 80, 0.9)' : 'rgba(244, 67, 54, 0.9)',
              color: 'white'
            }}
          />
        </Box>
      </Box>
      
      {/* Device Information - Styled like DefectSummarySection */}
      {showDeviceInfo && (
        <CardContent sx={{ 
          p: 2, 
          height: 90,
          bgcolor: 'rgba(0, 0, 0, 0.02)',
          borderTop: '1px solid',
          borderColor: 'divider'
        }}>
          <Stack spacing={0.5}>
            <Typography variant="subtitle2" fontWeight={600} noWrap>
              {device.device_name}
            </Typography>
            <Stack direction="row" spacing={1} alignItems="center">
              <LocationIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
              <Typography variant="caption" color="text.secondary" noWrap>
                {device.stream_network_name || device.network_name || 'Network not specified'}
                {device.stream_section_name && ` • ${device.stream_section_name}`}
              </Typography>
            </Stack>
            <Typography variant="caption" color="text.secondary" noWrap>
              Vehicle: {device.vehicle_id}
            </Typography>
          </Stack>
        </CardContent>
      )}
    </Card>
  );
};

export default StreamViewer;